import React from 'react';
import { FaWhatsapp, FaPhone, FaEnvelope, FaUser, FaGasPump } from 'react-icons/fa';
import useTheme from '../../hooks/useTheme';

const ContactInfo = ({ profile, isHighlighted = false, className = "", productDetails = null }) => {
  const theme = useTheme();

  const handleWhatsApp = () => {
    if (profile?.whatsapp) {
      let message = "Hello! I'm interested in your products. 👋\n\n";
      
      if (productDetails) {
        message += `📋 *Product Inquiry:*\n`;
        message += `🏷️ *Product:* ${productDetails.name}\n`;
        message += `💰 *Price:* ${productDetails.currency} ${productDetails.price}\n`;
        
        if (productDetails.description) {
          message += `📝 *Description:* ${productDetails.description}\n`;
        }
        
        if (productDetails.categoryName) {
          message += `📂 *Category:* ${productDetails.categoryName}\n`;
        }
        
        if (productDetails.subcategoryName) {
          message += `📁 *Subcategory:* ${productDetails.subcategoryName}\n`;
        }
        
        if (productDetails.brand) {
          message += `🏢 *Developer:* ${productDetails.brand}\n`;
        }
        
        if (productDetails.model) {
          message += `🔧 *Code:* ${productDetails.model}\n`;
        }
        
        // Add specifications if available
        if (productDetails.ProductSpecifications && Array.isArray(productDetails.ProductSpecifications)) {
          const specs = productDetails.ProductSpecifications
            .filter(spec => spec.key === "Payment Type" || spec.key === "Bedrooms" || spec.key === "Bathrooms" || spec.key === "Property Size")
            .map(spec => `• ${spec.key}: ${spec.value}`)
            .join('\n');
          
          if (specs) {
            message += `\n📋 *Specifications:*\n${specs}\n`;
          }
        }
        
        if (productDetails.warranty) {
          message += `📍 *Location:* ${productDetails.warranty}\n`;
        }
        
        message += `\nPlease provide more details about this product. Thank you! 🙏`;
      }
      
      const url = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank");
    }
  };

  const handleCall = () => {
    if (profile?.mobile) {
      window.open(`tel:${profile.mobile}`, "_blank");
    }
  };

  const handleEmail = () => {
    if (profile?.email) {
      let subject = "Product Inquiry";
      let body = "Hello! I'm interested in your products.\n\n";
      
      if (productDetails) {
        subject = `Product Inquiry - ${productDetails.name}`;
        body += `Product Inquiry Details:\n\n`;
        body += `Product: ${productDetails.name}\n`;
        body += `Price: ${productDetails.currency} ${productDetails.price}\n`;
        
        if (productDetails.description) {
          body += `Description: ${productDetails.description}\n`;
        }
        
        if (productDetails.categoryName) {
          body += `Category: ${productDetails.categoryName}\n`;
        }
        
        if (productDetails.subcategoryName) {
          body += `Subcategory: ${productDetails.subcategoryName}\n`;
        }
        
        if (productDetails.brand) {
          body += `Developer: ${productDetails.brand}\n`;
        }
        
        if (productDetails.model) {
          body += `Code: ${productDetails.model}\n`;
        }
        
        // Add specifications if available
        if (productDetails.ProductSpecifications && Array.isArray(productDetails.ProductSpecifications)) {
          const specs = productDetails.ProductSpecifications
            .filter(spec => spec.key === "Payment Type" || spec.key === "Bedrooms" || spec.key === "Bathrooms" || spec.key === "Property Size")
            .map(spec => `• ${spec.key}: ${spec.value}`)
            .join('\n');
          
          if (specs) {
            body += `\nSpecifications:\n${specs}\n`;
          }
        }
        
        if (productDetails.warranty) {
          body += `Location: ${productDetails.warranty}\n`;
        }
        
        body += `\nPlease provide more details about this product. Thank you!`;
      }
      
      const mailtoUrl = `mailto:${profile.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoUrl, "_blank");
    }
  };


  // Don't render if no contact info or brand is available
  const buttonSize = isHighlighted ? "w-10 h-10 md:w-12 md:h-12" : "w-8 h-8 md:w-10 md:h-10";
  const iconSize = isHighlighted ? "w-5 h-5 md:w-6 md:h-6" : "w-4 h-4 md:w-5 md:h-5";
  const textSize = isHighlighted ? "text-xs md:text-sm" : "text-xs";

  return (
    <div className={`w-full ${className}`} onClick={(e) => e.stopPropagation()}>
      {/* Footer Content */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Brand/User Info - Left Side - Only show if brand exists and is not empty */}
        {productDetails?.brand && productDetails.brand.trim() !== '' && (
          <div className="flex items-center gap-2 md:gap-3">
            <div 
              className={`${buttonSize} rounded-full flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-110 border-2`}
              style={{
                backgroundColor: '#4B5563',
                color: 'white',
                borderColor: '#000000'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#374151';
                e.target.style.borderColor = '#000000';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#4B5563';
                e.target.style.borderColor = '#000000';
              }}
            >
              <FaGasPump className={iconSize} />
            </div>
            <div className="flex flex-col">
              <span 
                className={`font-bold text-sm md:text-base text-gray-800`}
                style={{ 
                  textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)',
                  letterSpacing: '0.025em'
                }}
              >
                {productDetails.brand}
              </span>
            </div>
          </div>
        )}
        
        {/* Contact Buttons - Always Right Side */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3 ml-auto">
        {/* WhatsApp */}
        {profile?.whatsapp && (
          <button
            onClick={handleWhatsApp}
            className={`${buttonSize} text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-110`}
            style={{
              backgroundColor: theme.colors.primary || '#6B7280',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryHover || theme.colors.primary || '#4B5563';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary || '#6B7280';
            }}
            title="Contact via WhatsApp"
          >
            <FaWhatsapp className={iconSize} />
          </button>
        )}

        {/* Phone */}
        {profile?.mobile && (
          <button
            onClick={handleCall}
            className={`${buttonSize} text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-110`}
            style={{
              backgroundColor: theme.colors.primary || '#6B7280',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryHover || theme.colors.primary || '#4B5563';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary || '#6B7280';
            }}
            title="Call us"
          >
            <FaPhone className={`${iconSize} transform rotate-90`} />
          </button>
        )}

        {/* Email */}
        {profile?.email && (
          <button
            onClick={handleEmail}
            className={`${buttonSize} text-white rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg hover:scale-110`}
            style={{
              backgroundColor: theme.colors.primary || '#6B7280',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = theme.colors.primaryHover || theme.colors.primary || '#4B5563';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = theme.colors.primary || '#6B7280';
            }}
            title="Send email"
          >
            <FaEnvelope className={iconSize} />
          </button>
        )}

        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
